import React, { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check, ExternalLink, PlugZap, Unplug } from "lucide-react";

/**
 * Compact status card + connect/disconnect flow for Google Classroom.
 * Renders only for admins/managers (parent decides when to show).
 */
export default function GoogleClassroomConnect({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/google/status");
      setStatus(data);
    } catch (e) {
      // silent — non-admins may get 403; that's fine
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // React to the query param after Google callback
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected") === "1") {
      toast.success("Google Classroom verbunden.");
      window.history.replaceState(null, "", window.location.pathname);
      load();
    } else if (params.get("google_error")) {
      toast.error(`Google-Verbindung abgebrochen: ${params.get("google_error")}`);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const connect = () => {
    // Redirect the whole page so cookies + Google OAuth flow work naturally
    window.location.href = `${API}/google/oauth/start`;
  };

  const disconnect = async () => {
    if (!window.confirm("Google Classroom Verbindung wirklich trennen?")) return;
    try {
      await api.post("/google/oauth/disconnect");
      toast.success("Verbindung getrennt.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (loading || !status) return null;

  const connected = status.connected;
  return (
    <div
      className={`border ${connected ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"} p-4 flex items-center justify-between gap-4 flex-wrap`}
      data-testid="google-classroom-status"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 flex items-center justify-center ${connected ? "bg-emerald-500" : "bg-slate-300"} text-white`}>
          {connected ? <Check size={18} strokeWidth={2} /> : <PlugZap size={18} strokeWidth={1.5} />}
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Google Classroom</div>
          <div className="text-sm font-medium text-slate-900">
            {connected ? "Verbunden" : "Nicht verbunden"}
          </div>
          {!compact && !connected && (
            <div className="text-[11px] text-slate-500 mt-1">
              Autorisiere einmalig, damit offene Aufgaben im Teilnehmer-Portal angezeigt werden.
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {connected ? (
          <>
            <button
              onClick={connect}
              className="px-3 py-2 border border-slate-300 text-xs hover:bg-white"
              title="Erneut verbinden falls Berechtigungen geändert wurden"
              data-testid="google-reconnect-btn"
            >
              Erneut verbinden
            </button>
            <button
              onClick={disconnect}
              className="px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-1"
              data-testid="google-disconnect-btn"
            >
              <Unplug size={12} strokeWidth={1.5} /> Trennen
            </button>
          </>
        ) : (
          <button
            onClick={connect}
            className="px-4 py-3 bg-[#1a73e8] text-white text-sm font-medium hover:bg-[#1557b0] flex items-center gap-2"
            data-testid="google-connect-btn"
          >
            <ExternalLink size={14} strokeWidth={1.5} />
            Google Classroom verbinden
          </button>
        )}
      </div>
    </div>
  );
}
